import {
  faInfoCircle,
  faSync,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import NumberFormat from "react-number-format";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import useSWRFetchPagination from "../../../../helpers/hooks/useSWRFetchPagination";
import { isIterableArray } from "../../../../helpers/utils";
import ButtonIcon from "../../../common/ButtonIcon";
import { findMyServicesAsManager } from "../../../../helpers/api/finance";
import { findAllProperties } from "../../../../helpers/api/condominium";
import ButtonIconTooltip from "../../../common/ButtonIconTooltip";
import Flex from "../../../common/Flex";
import PageHeader from "../../../common/PageHeader";
import FullPagination from "../../../common/pagination/FullPagination";
import { Message, Or, PreloadState } from "../../../UI/preloading/PreloadState";
import handleErrorMessage from "../../../../helpers/errors/handleErrorMessage";
import moment from "moment";

const CondominiumStatus = () => {
  //Variable para consultar el condominio
  // eslint-disable-next-line
  const { id } = useParams();

  const [params] = useState({ condominiumId: id });
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState(null);
  const {
    data,
    error,
    isLoading,
    mutate,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(findMyServicesAsManager, params);

  useEffect(() => {
    if (id) {
      setLoading(true);
      findAllProperties(id)
        .then(({ meta: { total } }) => setProperties(total))
        .catch((err) => handleErrorMessage(err))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const condominio = data.filter(
    (dat) => dat?.condominium?.condominium_id === Number(id)
  );

  console.log(condominio);

  const columns = [
    {
      dataField: "description",
      text: "Servicio",
      classes: "border-0 align-middle p-3",
      formatter: (description, { charge: { number_properties } }) => (
        <p className="mb-0">
          <b>{number_properties} propiedades</b>
        </p>
      ),
      headerClasses: "border-0",
    },
    {
      dataField: "price",
      text: "Precio por propiedad",
      formatter: (field, { charge: { price } }) => (
        <NumberFormat
          displayType="text"
          thousandSeparator
          decimalScale={2}
          fixedDecimalScale
          prefix="$ "
          value={price}
        />
      ),
      classes: "border-0 align-middle text-center",
      headerClasses: "border-0 text-center",
    },
    {
      dataField: "total",
      text: "Total",
      formatter: (field, { charge: { price } }) =>
        loading ? (
          <Spinner size="sm" />
        ) : (
          <NumberFormat
            displayType="text"
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            prefix="$ "
            value={price * properties}
          />
        ),
      classes: "border-0 align-middle text-center",
      headerClasses: "border-0 text-center",
    },
    {
      dataField: "created_at",
      text: "Fecha de creación",
      formatter: (data, { charge: { created_at } }) => {
        return (
          <Flex column align="center" justify="center">
            {moment(created_at).format("DD/MM/YYYY")}
            <b>{moment(created_at).fromNow()}</b>
          </Flex>
        );
      },
      classes: "border-0 align-middle text-center",
      headerClasses: "border-0 text-center",
    },
    {
      dataField: "status",
      text: "Estado de servicio",
      classes: "border-0 align-middle",
      formatter: (status, { charge: { paid } }) =>
        !!paid ? (
          <Badge color="soft-success" className="px-3 fs--1" pill>
            Pagado
          </Badge>
        ) : (
          <Badge color="soft-danger" className="px-3 fs--1" pill>
            No pagado
          </Badge>
        ),
      headerClasses: "border-0",
      align: "center",
      headerAlign: "center",
    },
    {
      dataField: "options",
      text: "Opciones",
      classes: "border-0 align-middle",
      formatter: (field, { charge: { id } }) => {
        return (
          <>
            <UncontrolledDropdown direction="left">
              <DropdownToggle color="falcon-default" size="sm">
                <FontAwesomeIcon icon="ellipsis-h" />
              </DropdownToggle>
              <DropdownMenu right>
                <DropdownItem tag={Link} to={`payments/${id}`}>
                  Ver detalle
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </>
        );
      },
      headerClasses: "border-0",
      align: "center",
      headerAlign: "center",
    },
  ];

  return (
    <>
      <PageHeader
        title="Estado de servicio del condominio"
        titleTag="h2"
        description="Visualiza los servicio contratado por el condominio."
        className="mb-3"
        col={{ lg: 9 }}
      />
      <Card>
        <CardHeader className="bg-light">
          <Flex align="center" justify="between">
            <Flex align="center">
              <h5 className="mb-0 mr-2">
                Servicios contratados del condominio
              </h5>
              <ButtonIconTooltip
                color="falcon-default"
                placement="top"
                size="sm"
                id="refresh"
                icon={faSync}
                onClick={() => mutate()}
              >
                Actualizar
              </ButtonIconTooltip>
            </Flex>
            <ButtonIcon
              icon={faUndo}
              color="falcon-default"
              tag={Link}
              to={`/root/condominiums`}
            >
              Volver a condominios
            </ButtonIcon>
          </Flex>
        </CardHeader>
        <CardBody className="fs--1 p-0">
          <PreloadState cond={isIterableArray(condominio)} loading={isLoading}>
            <BootstrapTable
              value={true}
              data={condominio}
              bootstrap4
              columns={columns}
              keyField="id"
              bordered={false}
              classes="table-dashboard table-sm fs--1 border-bottom border-200 mb-0 table-dashboard-th-nowrap"
              rowClasses="btn-reveal-trigger border-top border-200 py-3"
              headerClasses="text-900 border-y border-200"
              wrapperClasses="table-responsive"
            />
            <Or value={false} cond={!!error}>
              <Message
                value={false}
                color={"info"}
                className="p-3"
                align="left"
                icon={faInfoCircle}
              >
                Aún no hay pagos para mostrar.
              </Message>
              <Or value={true}>
                <Alert color="danger" className="mb-0">
                  Esta página no está disponible
                </Alert>
              </Or>
            </Or>
          </PreloadState>
        </CardBody>
        {isIterableArray(condominio) && (
          <CardFooter className="border-top">
            <FullPagination
              nextPage={nextPage}
              prevPage={previousPage}
              goToPage={goToPage}
              meta={meta}
              className={"shadow-none"}
              posText={"servicios"}
              perPage={perPage}
            />
          </CardFooter>
        )}
      </Card>
    </>
  );
};

export default CondominiumStatus;
